import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Loader2, FileText, DollarSign, Eye } from "lucide-react";
import { usePdfPreview } from "@/components/PdfPreviewDialog";

const BRAND_NAVY = "#0B132B";
const BRAND_GOLD = "#D4AF37";

const fmt = (v, cost) => {
  if (v === null || v === undefined || v === "") return "";
  if (cost) {
    const n = typeof v === "number" ? v : Number(String(v).replace(/[$,]/g, ""));
    if (Number.isFinite(n)) return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return String(v);
};

export default function PublicRateCard() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const { preview, dialog: pdfDialog } = usePdfPreview();
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/rate-card/${token}/pdf`;
  const previewPdf = () => preview({
    srcUrl: pdfUrl,
    filename: `${data?.product_name || "rate-card"}.pdf`,
    title: `Rate Card · ${data?.product_name || ""}`,
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/rate-card/${token}`);
        setData(data);
      } catch (e) {
        setErr(formatApiError(e?.response?.data?.detail) || "Rate card not found");
      }
    })();
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-6">
        <Card className="p-8 max-w-md border-[#E9ECEF] text-center" data-testid="public-ratecard-error">
          <FileText size={28} className="mx-auto text-[#6C757D] mb-2" />
          <div className="font-bold text-[#0B132B]">{err}</div>
          <p className="text-xs text-[#6C757D] mt-2">The rate card link you followed is invalid or has expired. Please contact the sender.</p>
        </Card>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center text-[#6C757D]"><Loader2 className="animate-spin mr-2" size={16}/> Loading rate card…</div>;
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 sm:py-12" data-testid="public-ratecard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between gap-6 pb-6 mb-6 border-b" style={{ borderColor: BRAND_GOLD }}>
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] font-bold" style={{ color: BRAND_GOLD }}>
              Advantage Warranty Group · Rate Card
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight leading-tight mt-2" style={{ color: BRAND_NAVY }}>
              {data.product_name}
            </h1>
            {data.product_description && (
              <p className="text-sm text-[#6C757D] mt-2 max-w-xl leading-relaxed">{data.product_description}</p>
            )}
          </div>
          <div className="bg-white p-4 rounded-sm border border-[#E9ECEF] min-w-[260px]" data-testid="public-ratecard-contact">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1" style={{ color: BRAND_GOLD }}>Prepared by</div>
            <div className="font-bold text-sm" style={{ color: BRAND_NAVY }}>{data.agent?.name || data.agent?.email}</div>
            {data.agent?.agency && <div className="text-xs text-[#6C757D]">{data.agent.agency}</div>}
            <div className="text-xs text-[#6C757D] mt-1">
              <a href={`mailto:${data.agent?.email}`} className="hover:underline">{data.agent?.email}</a>
            </div>
            {data.agent?.phone && <div className="text-xs text-[#6C757D]">{data.agent.phone}</div>}
            {data.dealer_name && (
              <>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold mt-3 mb-1" style={{ color: BRAND_GOLD }}>Prepared for</div>
                <div className="font-bold text-sm" style={{ color: BRAND_NAVY }}>{data.dealer_name}</div>
              </>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 mb-6">
          <button
            type="button"
            onClick={previewPdf}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-sm bg-[#0B132B] text-white text-sm font-bold hover:bg-[#0B132B]/90"
            data-testid="public-ratecard-download-pdf"
          >
            <Eye size={14} /> Preview & Download
          </button>
          <span className="text-xs text-[#6C757D]">Shared {new Date(data.created_at).toLocaleDateString()}</span>
        </div>
        {pdfDialog}

        {/* Tables */}
        {(data.tables || []).length === 0 ? (
          <Card className="p-8 border-dashed text-center text-sm text-[#6C757D]">No rates published for this program yet.</Card>
        ) : (
          <div className="space-y-8" data-testid="public-ratecard-tables">
            {data.tables.map((t, i) => {
              const costCols = new Set(t.cost_columns || []);
              return (
                <section key={t.name || `tbl-${i}`} data-testid={`public-ratecard-table-${i}`}>
                  <div className="text-[11px] uppercase tracking-[0.22em] font-bold mb-2" style={{ color: BRAND_NAVY }}>{t.name}</div>
                  <div className="overflow-x-auto border border-[#E9ECEF] rounded-sm bg-white">
                    <table className="w-full text-xs">
                      <thead>
                        <tr style={{ background: BRAND_NAVY, color: "white" }}>
                          {(t.headers || []).map((h, hi) => (
                            <th key={`${t.name}-h${hi}`} className={`px-3 py-2 font-bold uppercase tracking-[0.08em] ${costCols.has(hi) ? "text-right" : "text-left"}`}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                          {(t.rows || []).map((r, ri) => (
                            <tr key={`${t.name}-row-${ri}`} className={ri % 2 ? "bg-[#F8F9FA]" : "bg-white"}>
                              {r.map((c, ci) => (
                                <td key={`${t.name}-r${ri}-c${ci}`} className={`px-3 py-1.5 border-t border-[#E9ECEF] ${costCols.has(ci) ? "text-right font-bold" : ""}`} style={{ color: BRAND_NAVY }}>
                                  {fmt(c, costCols.has(ci))}
                                </td>
                              ))}
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              );
            })}
          </div>
        )}

        {data.notes && (
          <div className="mt-8 p-4 bg-white border border-[#E9ECEF] rounded-sm">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-2" style={{ color: BRAND_GOLD }}>Notes</div>
            <p className="text-sm whitespace-pre-line" style={{ color: BRAND_NAVY }}>{data.notes}</p>
          </div>
        )}

        <div className="text-[11px] text-[#6C757D] mt-8 pt-6 border-t border-[#E9ECEF]">
          Rates subject to underwriting and program terms. Questions?{" "}
          <a href={`mailto:${data.agent?.email}`} className="font-bold" style={{ color: BRAND_NAVY }}>Reply to {data.agent?.name || data.agent?.email}</a>.
        </div>
      </div>
    </div>
  );
}
